import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { keyBy, map, pick, flatMap, concat } from 'lodash';
import { RbacApi } from 'api/rbac';
import { GENERAL_API_STALE } from 'config/query-config';
const PermissionSetDefinitionsKey = [
    'permission-set-definition'
];
const useGetEntityAccessQueryKey = (entityIds, pageSourceType)=>{
    return [
        'entity-access',
        entityIds,
        pageSourceType
    ];
};
export const usePermissionSetDefinitionsQuery = (options)=>{
    return useQuery(PermissionSetDefinitionsKey, async ()=>{
        return RbacApi.getPermissionSetDefinitions();
    }, options);
};
// Function to generate the query key
const generatePermissionSetMapQueryKey = (extraQueryParams = {})=>{
    return [
        'permission-set-map',
        JSON.stringify(extraQueryParams)
    ];
};
const permissionsToMap = (permissions)=>{
    return Object.values(permissions).reduce((permissionNameMap, resourcePermissions)=>{
        return {
            ...permissionNameMap,
            ...keyBy(resourcePermissions, 'permissionName')
        };
    }, {});
};
export const usePermissionSetDefinitionsMapQuery = (extraQueryParams, options)=>{
    return useQuery(generatePermissionSetMapQueryKey(extraQueryParams), async ()=>{
        return RbacApi.getPermissionSetDefinitions(extraQueryParams).then((permissionSets)=>{
            return permissionSets.reduce((permissionSetMap, { permissionSetId, permission, defaultProjectPermission })=>{
                permissionSetMap[permissionSetId] = {
                    defaultProjectPermission,
                    permissionMap: permissionsToMap(permission)
                };
                return permissionSetMap;
            }, {});
        });
    }, options);
};
export const usePermissionSetDefinitionsForCtxMap = ()=>{
    return usePermissionSetDefinitionsMapQuery({
        inCurrentUserCtx: 'true'
    }, {
        staleTime: Infinity
    });
};
const PermissionSetMapWithNameKey = [
    'permission-set-map-with-name'
];
const PermissionSetLightMapWithNameKey = [
    'permission-set-map-light'
];
export const usePermissionSetDefintionWithNamesMapQuery = (options)=>{
    return useQuery(PermissionSetMapWithNameKey, async ()=>{
        return RbacApi.getPermissionSetDefinitions().then((permissionSets)=>{
            return map(permissionSets, (entry)=>{
                var _ref;
                const permissions = flatMap(entry.permission, (permissionList)=>{
                    return map(permissionList, (permission)=>{
                        return pick(permission, [
                            'permissionName',
                            'requestScope',
                            'actionScopes'
                        ]);
                    });
                });
                if (entry.defaultProjectPermission) {
                    var _ref1;
                    var _permissionSets_find;
                    const linkedPermissions = flatMap(permissionSets === null || permissionSets === void 0 ? void 0 : (_permissionSets_find = permissionSets.find((permissionSet)=>permissionSet.permissionSetId === entry.defaultProjectPermission)) === null || _permissionSets_find === void 0 ? void 0 : _permissionSets_find.permission, (permissionList)=>{
                        return map(permissionList, (permission)=>{
                            return pick(permission, [
                                'permissionName',
                                'requestScope',
                                'actionScopes'
                            ]);
                        });
                    });
                    const defaultProjectPermissionSetDetails = permissionSets === null || permissionSets === void 0 ? void 0 : permissionSets.find((permissionSet)=>permissionSet.permissionSetId === entry.defaultProjectPermission);
                    return {
                        permissionSetId: entry === null || entry === void 0 ? void 0 : entry.permissionSetId,
                        permissionSetName: entry === null || entry === void 0 ? void 0 : entry.permissionSetName,
                        permissionSetDescription: entry === null || entry === void 0 ? void 0 : entry.permissionSetDescription,
                        permissions: concat(permissions, linkedPermissions),
                        permissionSetSpecificTo: entry.permissionSetSpecificTo,
                        resource: (_ref1 = entry === null || entry === void 0 ? void 0 : entry.resource) !== null && _ref1 !== void 0 ? _ref1 : 'PROJECT',
                        default: entry.default,
                        defaultProjectPermissionDetails: {
                            permissionSetId: defaultProjectPermissionSetDetails === null || defaultProjectPermissionSetDetails === void 0 ? void 0 : defaultProjectPermissionSetDetails.permissionSetId,
                            permissionSetName: defaultProjectPermissionSetDetails === null || defaultProjectPermissionSetDetails === void 0 ? void 0 : defaultProjectPermissionSetDetails.permissionSetName
                        },
                        permissionSetType: entry === null || entry === void 0 ? void 0 : entry.permissionSetType,
                        resourceVisibility: entry === null || entry === void 0 ? void 0 : entry.resourceVisibility
                    };
                }
                return {
                    permissionSetId: entry === null || entry === void 0 ? void 0 : entry.permissionSetId,
                    permissionSetName: entry === null || entry === void 0 ? void 0 : entry.permissionSetName,
                    permissionSetDescription: entry === null || entry === void 0 ? void 0 : entry.permissionSetDescription,
                    permissionSetSpecificTo: entry === null || entry === void 0 ? void 0 : entry.permissionSetSpecificTo,
                    permissions,
                    resource: (_ref = entry === null || entry === void 0 ? void 0 : entry.resource) !== null && _ref !== void 0 ? _ref : 'PROJECT',
                    default: entry.default,
                    permissionSetType: entry === null || entry === void 0 ? void 0 : entry.permissionSetType,
                    resourceVisibility: entry === null || entry === void 0 ? void 0 : entry.resourceVisibility
                };
            });
        });
    }, options);
};
export const usePermissionSetDefinitionLight = (options)=>{
    return useQuery(PermissionSetLightMapWithNameKey, async ()=>{
        return RbacApi.getPermissionSetDefinitionsLight().then((permissionSets)=>{
            return map(permissionSets, (entry)=>{
                var _ref;
                return {
                    permissionSetId: entry === null || entry === void 0 ? void 0 : entry.permissionSetId,
                    permissionSetName: entry === null || entry === void 0 ? void 0 : entry.permissionSetName,
                    permissionSetDescription: entry === null || entry === void 0 ? void 0 : entry.permissionSetDescription,
                    permissionSetSpecificTo: entry === null || entry === void 0 ? void 0 : entry.permissionSetSpecificTo,
                    resource: (_ref = entry === null || entry === void 0 ? void 0 : entry.resource) !== null && _ref !== void 0 ? _ref : 'PROJECT',
                    default: entry.default,
                    permissionSetType: entry === null || entry === void 0 ? void 0 : entry.permissionSetType,
                    permissionSetObjectType: entry === null || entry === void 0 ? void 0 : entry.permissionSetObjectType,
                    defaultProjectPermission: entry === null || entry === void 0 ? void 0 : entry.defaultProjectPermission
                };
            });
        });
    }, options);
};
export const useCreateCustomPermissionSetMutation = ()=>{
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (payload)=>RbacApi.createCustomPermissionSet(payload),
        onSuccess: ()=>{
            return queryClient.invalidateQueries([
                'permission-set-map-with-name'
            ]);
        }
    });
};
export const useUpdatePermissionSetMutation = ()=>{
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (payload)=>RbacApi.updatePermissionSet(payload),
        onSuccess: ()=>{
            return queryClient.invalidateQueries([
                'permission-set-map-with-name'
            ]);
        }
    });
};
export const useGetEntityAccessQuery = (entityIds, pageSourceType, queryOptions = {})=>useQuery(useGetEntityAccessQueryKey(entityIds, pageSourceType), ()=>RbacApi.getEntityAccess(entityIds, pageSourceType), {
        staleTime: GENERAL_API_STALE,
        ...queryOptions
    });
